<template>
  <div class="container">
    <header class="text-center wizard-header-text">
      <h1>Assessment Wizard</h1>
      <p class="gb-text-2">Discover targeted baseline and progress monitoring assessments from thousands of resources in our collection. </p>
    </header>

    <!-- Return to Wizard Button - Only visible during loading -->
    <div v-if="loading" class="return-button-container">
      <button class="return-button" @click="handleReturnToWizard">
        <i class="material-icons-round arrow-left">arrow_back</i>
        Return to Wizard
      </button>
    </div>

    <AssessmentWizardOverlay :theme="loading ? 'white' : 'gray'">
      <AssessmentLoadingScreen v-if="loading" />
      <form
        v-else
        @submit.prevent="handleSubmit"
        class="wizard-form">
        <!-- Form Inputs Container -->
        <div class="form-inputs-container">
          <!-- Grade Level -->
          <div class="form-field">
            <label for="grade-filter-btn" class="form-label">Grade Level</label>
            <a11y-dropdown
              :dropdown-props="{ tag: 'div' }"
              :button-props="{ id: 'grade-filter-btn', 'data-role': 'trigger', class: 'dropdown-toggle btn btn-default' }"
              :dom-attributes="{ role: 'menu', id: 'grade-dropdown' }">
              <template slot="button-contents">
                <span :class="{ 'text-muted': !formData.filters.gradeLevel }">
                  {{ gradeSelectorText }}
                  <span class="caret" />
                </span>
              </template>
              <template slot="dropdown-items">
                <li
                  class="dropdown-item"
                  role="menuitem">
                  <a
                    role="button"
                    @click="updateFilters({ field: 'gradeLevel', value: '' })">
                    Any grade
                  </a>
                </li>
                <li
                  v-for="grade in grades"
                  :key="grade"
                  class="dropdown-item"
                  role="menuitem">
                  <a
                    role="button"
                    @click="updateFilters({ field: 'gradeLevel', value: grade })">
                    {{ grade }}
                  </a>
                </li>
              </template>
            </a11y-dropdown>
          </div>

          <!-- Natural Language Query -->
          <div class="form-field">
            <label for="assessment-query" class="form-label">Describe the assessment you need</label>
            <textarea
              id="assessment-query"
              ref="queryTextarea"
              v-model="formData.query"
              rows="3"
              class="form-textarea"
              placeholder="Example: Phonics assessments for consonant blends and digraphs"
              @input="autoExpandTextarea"
            />
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="!isFormValid">
            Find Assessments
          </button>
        </div>
      </form>
    </AssessmentWizardOverlay>

    <!-- AI Integration Note -->
    <div class="ai-disclaimer-container">
      <AIDisclaimer />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import A11yDropdown from '@/components/A11yDropdown.vue'
import AssessmentWizardOverlay from '@/components/AssessmentWizardOverlay.vue'
import AssessmentLoadingScreen from './AssessmentLoadingScreen.vue'
import AIDisclaimer from '@/components/AIDisclaimer.vue'
import { assessmentGradesSet } from './assessments_filter_options'

export default {
  name: 'AssessmentWizard',
  components: {
    AssessmentWizardOverlay,
    A11yDropdown,
    AssessmentLoadingScreen,
    AIDisclaimer,
  },
  data() {
    return {
      formData: {
        query: '',
        files: [],
        filters: {
          subject: '',
          gradeLevel: '',
          category: '',
        },
      },
      selectedSubject: null,
    }
  },
  computed: {
    ...mapGetters({
      subjects: 'contentProfile/publishedSubjects',
      loading: 'agenticSearch/loading',
      hasResults: 'agenticSearch/hasAssessmentResults'
    }),
    subjectSelectorText() {
      return this.selectedSubject ? this.selectedSubject.subject : 'Select Subject'
    },
    categories() {
      return (this.selectedSubject && this.selectedSubject.categories) || []
    },
    grades() {
      return assessmentGradesSet
    },
    gradeSelectorText() {
      if (!this.formData.filters.gradeLevel) {
        return 'grade'
      }
      const grade = this.grades.find(g => g === this.formData.filters.gradeLevel)
      return grade ? grade : 'grade'
    },
    categorySelectorText() {
      if (!this.formData.filters.category) {
        return 'Select Category'
      }
      const category = this.categories.find(c => c === this.formData.filters.category)
      return category ? category : 'Select Category'
    },
    isFormValid() {
      const hasQuery = this.formData.query.trim().length > 0
      const hasGradeLevel = !!this.formData.filters.gradeLevel
      return hasQuery || hasGradeLevel
    },
  },
  methods: {
    ...mapActions({
      searchAssessments: 'agenticSearch/searchAssessments',
    }),
    autoExpandTextarea() {
      const textarea = this.$refs.queryTextarea
      if (!textarea) return

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto'

      // Set height based on scrollHeight, with a max of 200px
      const newHeight = Math.min(textarea.scrollHeight, 200)
      textarea.style.height = newHeight + 'px'
    },
    handleReturnToWizard() {
      // Clear store results and abort any in-flight search (eventSource.close() in clearResults)
      this.$store.dispatch('agenticSearch/clearResults')

      // Reset component-local form state
      this.formData.query = ''
      this.formData.files = []
      this.formData.filters.subject = ''
      this.formData.filters.gradeLevel = ''
      this.formData.filters.category = ''
      this.selectedSubject = null
    },
    updateFilters({ field, value }) {
      if (field === 'subject') {
        this.selectedSubject = this.subjects.find(s => s.subject === value) || null
        this.formData.filters.subject = value
        this.formData.filters.category = ''
        this.formData.filters.gradeLevel = ''
      } else {
        this.formData.filters[field] = value
      }
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files)
      this.formData.files = [...this.formData.files, ...files]
      event.target.value = ''
    },
    removeFile(index) {
      this.formData.files.splice(index, 1)
    },
    async handleSubmit() {
      try {
        await this.searchAssessments({
          query: this.formData.query,
          files: this.formData.files,
          filters: this.formData.filters,
        })
      } catch (error) {
        console.error('Error searching assessments:', error)
      }
    },
  },
  watch: {
    hasResults(hasResults) {
      if (hasResults) {
        this.$router.push('/browse/assessments?ref=wizard')
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '@/variables.less';

.wizard-header-text {
  h1 {
    width: 100%;
    margin: 10px 0;
    display: flex;
    justify-content: center;
  }

  margin-bottom: 24px;
}

.return-button-container {
  display: flex;
  justify-content: center;
  margin: 24px 0 12px 0;
}

.return-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 24px;
  border: 1px solid #007dba;
  border-radius: 4px;
  background: transparent;
  color: #007dba;
  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 125, 186, 0.05);
  }

  &:focus {
    outline: 2px solid #007dba;
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #007dba;
    outline-offset: 2px;
  }

  .arrow-left {
    font-size: 24px;
    line-height: 1;
  }
}

.form-inputs-container {
  display: flex;
  width: 576px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
}

.form-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.form-label {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 36px;
  color: #0a0a0a;
  margin: 0;
}

.btn.btn-default.dropdown-toggle {
  display: flex;
  width: 200px;
  align-items: flex-start;
  border-radius: 4px;
  border: 1px solid #adadad;
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  height: 44px;
  padding: 12px;
  font-size: 18px;
  font-weight: 400;
  line-height: 18px;
  color: #6e6e6e;

  &:focus {
    outline: 2px solid #007dba;
    outline-offset: 2px;
    border-color: #007dba;
  }

  &:focus:not(:focus-visible) {
    outline: none;
    border-color: #adadad;
  }

  &:focus-visible {
    outline: 2px solid #007dba;
    outline-offset: 2px;
    border-color: #007dba;
  }
}

.form-textarea {
  width: 100%;
  min-height: 116px;
  max-height: 200px;
  padding: 12px;
  border: 1px solid #adadad;
  border-radius: 4px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  font-size: 18px;
  font-weight: 400;
  line-height: 18px;
  resize: none;
  margin-bottom: 36px;
  overflow-y: auto;
  transition: height 0.1s ease;

  &::placeholder {
    color: #6e6e6e;
  }

  &:hover {
    border-color: #adadad;
  }

  &:focus {
    outline: 2px solid #007dba;
    outline-offset: 2px;
    border-color: #007dba;
  }

  &:focus:not(:focus-visible) {
    outline: none;
    border-color: #adadad;
  }

  &:focus-visible {
    outline: 2px solid #007dba;
    outline-offset: 2px;
    border-color: #007dba;
  }
}

.ai-disclaimer-container {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.btn-primary {
  display: flex;
  height: 48px;
  padding: 12px 24px;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  font-size: 18px;
}

</style>
